import { Injectable } from '@angular/core';
import {
  ToolProvider,
  ToolProviderAuthType,
  ToolProviderTokenStatus,
  ProjectToolConnection,
  CopilotAutomation,
  ToolConnectionAudience,
} from 'src/models';

import { ApiService } from './api.service';

const BASE_PATH = '/external-tool-providers';
const CONNECTIONS_PATH = '/external-tool-connections';
const ME_CONNECTIONS_PATH = '/me/external-tool-connections';

// =============================================================================
// Provider params (tenant-level registry)
// =============================================================================

export interface UpdateToolProviderParams {
  description?: string | null;
  url?: string;
  authType?: ToolProviderAuthType;
  oauth?: {
    discoveryUrl?: string;
    authorizationUrl?: string;
    tokenUrl?: string;
    clientId?: string;
    clientSecret?: string;
    scopes?: string[];
  };
}

export interface CreateToolProviderParams extends UpdateToolProviderParams {
  name: string;
  url: string;
  authType: ToolProviderAuthType;
}

// =============================================================================
// Connection params (project-level)
// =============================================================================

export interface CreateConnectionParams {
  serverId: string;
  name: string;
  /** Who the connection serves. Fixed at create; the server defaults it to copilot. */
  audience?: ToolConnectionAudience;
  requireUserAuth?: boolean;
  apiKey?: {
    headerName: string;
    apiKey: string;
  };
}

export type LegacyCreateConnectionParams = CreateToolProviderParams & Omit<CreateConnectionParams, 'serverId'>;

export interface UpdateConnectionParams {
  name?: string;
  requireUserAuth?: boolean;
  enabled?: boolean;
  clearApiKey?: boolean;
  apiKey?: {
    headerName: string;
    apiKey: string;
  };
}

export type LegacyUpdateConnectionParams = UpdateConnectionParams & UpdateToolProviderParams;

// =============================================================================
// Response types
// =============================================================================

export interface InitiateOAuthResponse {
  authorizationUrl: string;
}

export interface HealthCheckResponse {
  healthy: boolean;
  toolCount?: number;
  error?: string;
}

export interface RefreshToolsResponse {
  toolCount: number;
  newTools: string[];
  removedTools: string[];
  changedTools: string[];
}

interface CredentialEndpointOptions {
  requireUserAuth?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ToolProvidersService {
  constructor(private apiService: ApiService) {}

  // =============================================================================
  // Provider CRUD (tenant-level)
  // =============================================================================

  /** List tenant-level providers (the Add Server source step and its registered-URL check) */
  async list(projectId: string): Promise<ToolProvider[]> {
    const response = await this.apiService.getPromise<{ data: ToolProvider[] }>(
      `/projects/${projectId}/external-tool-providers`,
    );

    return response.data;
  }

  /**
   * List connections for a project (for Connected tab). With an audience, only
   * the connections serving it; without one, every connection in the project.
   */
  async listConnections(
    projectId: string,
    options?: { audience?: ToolConnectionAudience },
  ): Promise<ProjectToolConnection[]> {
    const query = options?.audience ? `?audience=${options.audience}` : '';
    const response = await this.apiService.getPromise<{ data: ProjectToolConnection[] }>(
      `/projects/${projectId}/connections${query}`,
    );

    return response.data;
  }

  async getById(serverId: string): Promise<ToolProvider> {
    return this.apiService.getPromise<ToolProvider>(`${BASE_PATH}/${serverId}`);
  }

  async create(params: CreateToolProviderParams): Promise<ToolProvider> {
    return this.apiService.postPromise<ToolProvider>(BASE_PATH, params);
  }

  async update(serverId: string, params: UpdateToolProviderParams): Promise<ToolProvider> {
    return this.apiService.patchPromise<ToolProvider>(`${BASE_PATH}/${serverId}`, params);
  }

  async delete(serverId: string): Promise<void> {
    return this.apiService.deletePromise<void>(`${BASE_PATH}/${serverId}`);
  }

  // =============================================================================
  // Connection CRUD (project-level)
  // =============================================================================

  async createConnection(
    projectId: string,
    params: CreateConnectionParams | LegacyCreateConnectionParams,
  ): Promise<ProjectToolConnection> {
    return this.apiService.postPromise<ProjectToolConnection>(`/projects/${projectId}/connections`, params);
  }

  async assignToProject(
    serverId: string,
    projectId: string,
    params: Omit<CreateConnectionParams, 'serverId'>,
  ): Promise<void> {
    return this.apiService.postPromise<void>(`${BASE_PATH}/${serverId}/projects/${projectId}`, params);
  }

  async removeConnection(connectionId: string): Promise<void> {
    return this.apiService.deletePromise<void>(`${CONNECTIONS_PATH}/${connectionId}`);
  }

  async updateConnection(
    connectionId: string,
    params: UpdateConnectionParams | LegacyUpdateConnectionParams,
  ): Promise<{ success: true }> {
    return this.apiService.patchPromise<{ success: true }>(`${CONNECTIONS_PATH}/${connectionId}`, params);
  }

  // =============================================================================
  // Tool definition management
  // =============================================================================

  async updateToolDefinition(
    toolDefinitionId: string,
    params: { enabled?: boolean; availableInAllPlaybooks?: boolean; connectionId: string },
  ): Promise<void> {
    return this.apiService.patchPromise<void>(`/external-tool-definitions/${toolDefinitionId}`, params);
  }

  async refreshTools(serverId: string, connectionId?: string): Promise<RefreshToolsResponse> {
    const params = connectionId ? `?connectionId=${connectionId}` : '';

    return this.apiService.postPromise<RefreshToolsResponse>(`${BASE_PATH}/${serverId}/refresh-tools${params}`, {});
  }

  // =============================================================================
  // Connection-level auth endpoints
  // =============================================================================

  private credentialPath(connectionId: string, options?: CredentialEndpointOptions): string {
    return (options?.requireUserAuth ?? true)
      ? `${ME_CONNECTIONS_PATH}/${connectionId}`
      : `${CONNECTIONS_PATH}/${connectionId}`;
  }

  async initiateOAuth(
    connectionId: string,
    redirectUri: string,
    options?: CredentialEndpointOptions,
  ): Promise<InitiateOAuthResponse> {
    return this.apiService.postPromise<InitiateOAuthResponse>(
      `${this.credentialPath(connectionId, options)}/oauth/initiate`,
      {
        redirectUri,
      },
    );
  }

  async getTokenStatus(connectionId: string, options?: CredentialEndpointOptions): Promise<ToolProviderTokenStatus> {
    return this.apiService.getPromise<ToolProviderTokenStatus>(
      `${this.credentialPath(connectionId, options)}/token-status`,
    );
  }

  async submitUserApiKey(connectionId: string, apiKey: string): Promise<void> {
    return this.apiService.postPromise<void>(`${ME_CONNECTIONS_PATH}/${connectionId}/api-key`, { apiKey });
  }

  async deleteToken(connectionId: string, options?: CredentialEndpointOptions): Promise<void> {
    return this.apiService.deletePromise<void>(`${this.credentialPath(connectionId, options)}/token`);
  }

  async checkHealth(connectionId: string, options?: CredentialEndpointOptions): Promise<HealthCheckResponse> {
    const path = options ? this.credentialPath(connectionId, options) : `${CONNECTIONS_PATH}/${connectionId}`;

    return this.apiService.getPromise<HealthCheckResponse>(`${path}/health`);
  }

  // =============================================================================
  // Copilot Automations
  // =============================================================================

  async listCopilotAutomations(projectId: string): Promise<CopilotAutomation[]> {
    const response = await this.apiService.getPromise<{ data: CopilotAutomation[] }>(
      `/projects/${projectId}/copilot-automations`,
    );

    return response.data;
  }

  async toggleCopilotAutomation(automationId: string, enabled: boolean): Promise<void> {
    return this.apiService.patchPromise<void>(`/automations/${automationId}/copilot-enabled`, { enabled });
  }

  // =============================================================================
  // Helpers
  // =============================================================================

  formatRefreshSummary(result: RefreshToolsResponse): string {
    const parts: string[] = [];

    if (result.newTools.length > 0) {
      parts.push(`${result.newTools.length} new`);
    }

    if (result.changedTools.length > 0) {
      parts.push(`${result.changedTools.length} updated`);
    }

    if (result.removedTools.length > 0) {
      parts.push(`${result.removedTools.length} removed`);
    }

    const summary = parts.length > 0 ? parts.join(', ') : 'No changes';

    return `Tools refreshed: ${summary} (${result.toolCount} total)`;
  }
}
